import React from 'react';
import { View, Text, StyleSheet, Image } from '@react-pdf/renderer';
import axios from 'axios';
import { backendApiUrl } from '@/lib/config';
import oktaService from '@/services/OktaService';

const styles = StyleSheet.create({
    container: {
        padding: 10,
    },
    sectionTitle: {
        fontSize: 16,
        fontWeight: 'bold',
        marginBottom: 15,
        color: '#1e40af',
        borderBottomWidth: 1,
        borderBottomColor: '#e0e0e0',
        paddingBottom: 5,
    },
    contentContainer: {
        flexDirection: 'row',
        gap: 20,
    },
    leftColumn: {
        flex: 1,
        paddingRight: 10,
    },
    rightColumn: {
        flex: 1,
        paddingLeft: 10,
    },
    subsectionTitle: {
        fontSize: 14,
        fontWeight: 'bold',
        marginTop: 15,
        marginBottom: 10,
        color: '#374151',
        backgroundColor: '#f3f4f6',
        padding: 8,
    },
    detailsList: {
        marginBottom: 15,
    },
    detailItem: {
        flexDirection: 'row',
        marginBottom: 5,
        fontSize: 10,
    },
    detailLabel: {
        fontWeight: 'bold',
        width: '40%',
        color: '#374151',
    },
    detailValue: {
        width: '60%',
        color: '#111827',
    },
    shipTestDetailLabel: {
        fontWeight: 'bold',
        width: '75%',
        color: '#374151',
    },
    shipTestDetailValue: {
        width: '25%',
        color: '#111827',
    },
    palletImageContainer: {
        marginTop: 15,
        alignItems: 'center',
    },
    palletImage: {
        width: 200,
        height: 200,
        borderWidth: 1,
        borderColor: '#e5e7eb',
        borderRadius: 5,
    },
    palletImagePlaceholder: {
        width: 200,
        height: 200,
        backgroundColor: '#f3f4f6',
        borderWidth: 1,
        borderColor: '#d1d5db',
        borderStyle: 'dashed',
        justifyContent: 'center',
        alignItems: 'center',
        borderRadius: 5,
    },
    placeholderText: {
        fontSize: 10,
        color: '#6b7280',
        textAlign: 'center',
    },
    attachmentsSection: {
        marginTop: 15,
    },
    attachmentCategory: {
        marginBottom: 10,
    },
    attachmentTitle: {
        fontSize: 11,
        fontWeight: 'bold',
        marginBottom: 5,
        color: '#374151',
    },
    attachmentItem: {
        fontSize: 9,
        color: '#6b7280',
        marginBottom: 2,
        marginLeft: 10,
    },
    noAttachments: {
        fontSize: 9,
        color: '#9ca3af',
        fontStyle: 'italic',
        marginLeft: 10,
    },
});

interface OptionDetailsPDFProps {
    data: {
        optionNo: string;
        sourceType?: string;
        palletVariant?: string;
        numberOfPallets?: string;
        overallDecision?: string;
        product: string;
        brand?: string;
        category?: string;
        packageMaterial?: string;
        volume?: string;
        filledWeight?: string;
        diameter?: string;
        height?: string;
        bottleDesign?: string;
        itemsCase?: string;
        caseLength?: string;
        caseWidth?: string;
        caseHeight?: string;
        tertiaryPackageType?: string;
        tertiaryLength?: string;
        tertiaryWidth?: string;
        tertiaryHeight?: string;
        palletTypes?: string;
        palletMaterial?: string;
        unitLoadLength?: string;
        unitLoadWidth?: string;
        unitLoadHeight?: string;
        casesPerLayer?: string;
        layersPerPallet?: string;
        itemsPallet?: string;
        palletPatternStack?: string;
        palletImage?: string;
        testResults?: {
            preShipLean?: string;
            postShipLean?: string;
            postShipWalk?: string;
            postStackLean?: string;
            postStackCompressionRebound?: string;
        };
        ftl?: {
            preShip?: { top?: string; middle?: string; bottom?: string };
            postShip?: { top?: string; middle?: string; bottom?: string };
        };
        primaryFiles?: Array<{ filename: string; url: string }>;
        secondaryFiles?: Array<{ filename: string; url: string }>;
        unitFiles?: Array<{ filename: string; url: string }>;
    };
}

export const shouldShowPalletImageInReport = (data?: { sourceType?: string }) => (
    data?.sourceType !== 'physical'
);

function extractString(url) {
    const baseUrl = "https://dcsdeveus03sa.blob.core.windows.net/";
    const index = url.indexOf(baseUrl);

    if (index !== -1) {
        return url.substring(index + baseUrl.length);
    } else {
        return null; // Or handle the case where the base URL is not found
    }
}
export const OptionDetailsPDF: React.FC<OptionDetailsPDFProps> = ({ data }) => {
    const formatValue = (value: unknown) => {
        return value === undefined || value === null || value === '' ? 'N/A' : String(value);
    };
    const detailsTitle = data.sourceType === 'physical' ? 'Physical Ship Test Details' : 'Specification Details';
    const hasTertiaryDetails = Boolean(
        data.tertiaryPackageType || data.tertiaryLength || data.tertiaryWidth || data.tertiaryHeight
    );
    const hasShipTestResults = Boolean(
        data.testResults && Object.values(data.testResults).some((value) => value !== undefined && value !== null && value !== '')
    );
    const hasFtlResults = Boolean(
        data.ftl && (
            Object.values(data.ftl.preShip || {}).some((value) => value !== undefined && value !== null && value !== '') ||
            Object.values(data.ftl.postShip || {}).some((value) => value !== undefined && value !== null && value !== '')
        )
    );
    const shouldShowShipTestResults = data.sourceType === 'physical' && (hasShipTestResults || hasFtlResults);
    const physicalDetails = [
        ['Pallet Variant', data.palletVariant],
        ['Number Of Pallets', data.numberOfPallets],
        ['Overall Decision', data.overallDecision],
    ].filter(([, value]) => value !== undefined && value !== null && value !== '');
    const formatFtlValues = (values: { top?: string; middle?: string; bottom?: string } = {}) => (
        [values.top, values.middle, values.bottom].map(formatValue).join(':')
    );
    // const getPalletImage = async () => {
    //     try {
    //         const url = data.palletImage
    //         const imageName = extractString(url);
    //         const oktaUser = await oktaService.getUser()

    //         const response = await axios.get(`${backendApiUrl}/media/blob-info/${imageName}?containerName=distribution-analytics-files`, {
    //             headers: {
    //                 Authorization: `Bearer ${oktaUser.access_token}`
    //             }
    //         })
    //         console.log(response, 'responseeeeess')
    //         return response
    //     } catch (error) {
    //         console.log(error, 'image error')
    //         return null
    //     }
    // }
    // const palletImage = getPalletImage()
    return (
        <View style={styles.container}>
            <Text style={styles.sectionTitle}>
                {data.optionNo} - {detailsTitle}
            </Text>

            <View style={styles.contentContainer}>
                {/* Left Column */}
                <View style={styles.leftColumn}>
                    {/* Project Details */}
                    <Text style={styles.subsectionTitle}>Project Details</Text>
                    <View style={styles.detailsList}>
                        <View style={styles.detailItem}>
                            <Text style={styles.detailLabel}>Option ID / Spec Number:</Text>
                            <Text style={styles.detailValue}>{formatValue(data.optionNo)}</Text>
                        </View>
                        <View style={styles.detailItem}>
                            <Text style={styles.detailLabel}>Product Description:</Text>
                            <Text style={styles.detailValue}>{formatValue(data.product)}</Text>
                        </View>
                        <View style={styles.detailItem}>
                            <Text style={styles.detailLabel}>Brand:</Text>
                            <Text style={styles.detailValue}>{formatValue(data.brand)}</Text>
                        </View>
                        <View style={styles.detailItem}>
                            <Text style={styles.detailLabel}>Category:</Text>
                            <Text style={styles.detailValue}>{formatValue(data.category)}</Text>
                        </View>
                    </View>

                    {/* Primary Details */}
                    <Text style={styles.subsectionTitle}>Primary Package Details</Text>
                    <View style={styles.detailsList}>
                        <View style={styles.detailItem}>
                            <Text style={styles.detailLabel}>Package Material:</Text>
                            <Text style={styles.detailValue}>{formatValue(data.packageMaterial)}</Text>
                        </View>
                        <View style={styles.detailItem}>
                            <Text style={styles.detailLabel}>Volume (ml):</Text>
                            <Text style={styles.detailValue}>{formatValue(data.volume)}</Text>
                        </View>
                        <View style={styles.detailItem}>
                            <Text style={styles.detailLabel}>Filled Weight (g):</Text>
                            <Text style={styles.detailValue}>{formatValue(data.filledWeight)}</Text>
                        </View>
                        <View style={styles.detailItem}>
                            <Text style={styles.detailLabel}>Diameter (in):</Text>
                            <Text style={styles.detailValue}>{formatValue(data.diameter)}</Text>
                        </View>
                        <View style={styles.detailItem}>
                            <Text style={styles.detailLabel}>Height (in):</Text>
                            <Text style={styles.detailValue}>{formatValue(data.height)}</Text>
                        </View>
                        <View style={styles.detailItem}>
                            <Text style={styles.detailLabel}>Bottle Design:</Text>
                            <Text style={styles.detailValue}>{formatValue(data.bottleDesign)}</Text>
                        </View>
                    </View>

                    {/* Secondary Package Details */}
                    <Text style={styles.subsectionTitle}>Secondary Package & Tertiary Details</Text>
                    <View style={styles.detailsList}>
                        <View style={styles.detailItem}>
                            <Text style={styles.detailLabel}>Items / Case:</Text>
                            <Text style={styles.detailValue}>{formatValue(data.itemsCase)}</Text>
                        </View>
                        <View style={styles.detailItem}>
                            <Text style={styles.detailLabel}>Case Length (in):</Text>
                            <Text style={styles.detailValue}>{formatValue(data.caseLength)}</Text>
                        </View>
                        <View style={styles.detailItem}>
                            <Text style={styles.detailLabel}>Case Width (in):</Text>
                            <Text style={styles.detailValue}>{formatValue(data.caseWidth)}</Text>
                        </View>
                        <View style={styles.detailItem}>
                            <Text style={styles.detailLabel}>Case Height (in):</Text>
                            <Text style={styles.detailValue}>{formatValue(data.caseHeight)}</Text>
                        </View>
                        {hasTertiaryDetails && (
                            <>
                                <View style={styles.detailItem}>
                                    <Text style={styles.detailLabel}>Tertiary Package Type:</Text>
                                    <Text style={styles.detailValue}>{formatValue(data.tertiaryPackageType)}</Text>
                                </View>
                                <View style={styles.detailItem}>
                                    <Text style={styles.detailLabel}>Tertiary Length (in):</Text>
                                    <Text style={styles.detailValue}>{formatValue(data.tertiaryLength)}</Text>
                                </View>
                                <View style={styles.detailItem}>
                                    <Text style={styles.detailLabel}>Tertiary Width (in):</Text>
                                    <Text style={styles.detailValue}>{formatValue(data.tertiaryWidth)}</Text>
                                </View>
                                <View style={styles.detailItem}>
                                    <Text style={styles.detailLabel}>Tertiary Height (in):</Text>
                                    <Text style={styles.detailValue}>{formatValue(data.tertiaryHeight)}</Text>
                                </View>
                            </>
                        )}
                    </View>
                </View>

                {/* Right Column */}
                <View style={styles.rightColumn}>
                    {/* Pallet Details */}
                    <Text style={styles.subsectionTitle}>Pallet Details</Text>
                    <View style={styles.detailsList}>
                        <View style={styles.detailItem}>
                            <Text style={styles.detailLabel}>Pallet Dimension (inches):</Text>
                            <Text style={styles.detailValue}>{formatValue(data.palletTypes)}</Text>
                        </View>
                        <View style={styles.detailItem}>
                            <Text style={styles.detailLabel}>Pallet Material:</Text>
                            <Text style={styles.detailValue}>{formatValue(data.palletMaterial)}</Text>
                        </View>
                        <View style={styles.detailItem}>
                            <Text style={styles.detailLabel}>Unit Load L:W:H:</Text>
                            <Text style={styles.detailValue}>
                                {formatValue(data.unitLoadLength)} : {formatValue(data.unitLoadWidth)} : {formatValue(data.unitLoadHeight)}
                            </Text>
                        </View>
                        <View style={styles.detailItem}>
                            <Text style={styles.detailLabel}>Cases per Layer:</Text>
                            <Text style={styles.detailValue}>{formatValue(data.casesPerLayer)}</Text>
                        </View>
                        <View style={styles.detailItem}>
                            <Text style={styles.detailLabel}>Layers per Pallet:</Text>
                            <Text style={styles.detailValue}>{formatValue(data.layersPerPallet)}</Text>
                        </View>
                        <View style={styles.detailItem}>
                            <Text style={styles.detailLabel}>Items / Pallet:</Text>
                            <Text style={styles.detailValue}>{formatValue(data.itemsPallet)}</Text>
                        </View>
                        <View style={styles.detailItem}>
                            <Text style={styles.detailLabel}>Pallet Pattern:</Text>
                            <Text style={styles.detailValue}>{formatValue(data.palletPatternStack)}</Text>
                        </View>
                    </View>

                    {data.sourceType === 'physical' && physicalDetails.length > 0 && (
                        <>
                            <Text style={styles.subsectionTitle}>Physical Test Details</Text>
                            <View style={styles.detailsList}>
                                {physicalDetails.map(([label, value]) => (
                                    <View key={label} style={styles.detailItem}>
                                        <Text style={styles.detailLabel}>{`${label}:`}</Text>
                                        <Text style={styles.detailValue}>{formatValue(value)}</Text>
                                    </View>
                                ))}
                            </View>
                        </>
                    )}

                    {shouldShowShipTestResults && (
                        <>
                            <Text style={styles.subsectionTitle}>Ship Test Results</Text>
                            <View style={styles.detailsList}>
                                <View style={styles.detailItem}>
                                    <Text style={styles.shipTestDetailLabel}>Preship Lean (inches):</Text>
                                    <Text style={styles.shipTestDetailValue}>{formatValue(data.testResults?.preShipLean)}</Text>
                                </View>
                                <View style={styles.detailItem}>
                                    <Text style={styles.shipTestDetailLabel}>Preship FTL (T:M:B):</Text>
                                    <Text style={styles.shipTestDetailValue}>{formatFtlValues(data.ftl?.preShip)}</Text>
                                </View>
                                <View style={styles.detailItem}>
                                    <Text style={styles.shipTestDetailLabel}>Postship Lean (inches):</Text>
                                    <Text style={styles.shipTestDetailValue}>{formatValue(data.testResults?.postShipLean)}</Text>
                                </View>
                                <View style={styles.detailItem}>
                                    <Text style={styles.shipTestDetailLabel}>Postship FTL (T:M:B):</Text>
                                    <Text style={styles.shipTestDetailValue}>{formatFtlValues(data.ftl?.postShip)}</Text>
                                </View>
                                <View style={styles.detailItem}>
                                    <Text style={styles.shipTestDetailLabel}>Postship Walk (inches):</Text>
                                    <Text style={styles.shipTestDetailValue}>{formatValue(data.testResults?.postShipWalk)}</Text>
                                </View>
                                <View style={styles.detailItem}>
                                    <Text style={styles.shipTestDetailLabel}>Poststack Lean (inches):</Text>
                                    <Text style={styles.shipTestDetailValue}>{formatValue(data.testResults?.postStackLean)}</Text>
                                </View>
                                <View style={styles.detailItem}>
                                    <Text style={styles.shipTestDetailLabel}>Poststack Compression (inches):</Text>
                                    <Text style={styles.shipTestDetailValue}>{formatValue(data.testResults?.postStackCompressionRebound)}</Text>
                                </View>
                            </View>
                        </>
                    )}

                    {shouldShowPalletImageInReport(data) && (
                        <View style={styles.palletImageContainer}>
                            <Text style={[styles.subsectionTitle, { marginTop: 0, textAlign: 'center' }]}>
                                Pallet Image
                            </Text>
                            {data.palletImage ? (
                                <Image src={data.palletImage} style={styles.palletImage} />
                            ) : (
                                <View style={styles.palletImagePlaceholder}>
                                    <Text style={styles.placeholderText}>
                                        No pallet image{'\n'}available
                                    </Text>
                                </View>
                            )}
                        </View>
                    )}

                    {/* Attachments */}
                    {/* <View style={styles.attachmentsSection}>
                        <Text style={styles.subsectionTitle}>Attachments</Text>

                        <View style={styles.attachmentCategory}>
                            <Text style={styles.attachmentTitle}>Primary Package Files:</Text>
                            {data.primaryFiles && data.primaryFiles.length > 0 ? (
                                data.primaryFiles.map((file, index) => (
                                    <Text key={index} style={styles.attachmentItem}>
                                        • {file.filename}
                                    </Text>
                                ))
                            ) : (
                                <Text style={styles.noAttachments}>No primary files available</Text>
                            )}
                        </View>

                        <View style={styles.attachmentCategory}>
                            <Text style={styles.attachmentTitle}>Secondary Package Files:</Text>
                            {data.secondaryFiles && data.secondaryFiles.length > 0 ? (
                                data.secondaryFiles.map((file, index) => (
                                    <Text key={index} style={styles.attachmentItem}>
                                        • {file.filename}
                                    </Text>
                                ))
                            ) : (
                                <Text style={styles.noAttachments}>No secondary files available</Text>
                            )}
                        </View>

                        <View style={styles.attachmentCategory}>
                            <Text style={styles.attachmentTitle}>Pallet/Unit Load Files:</Text>
                            {data.unitFiles && data.unitFiles.length > 0 ? (
                                data.unitFiles.map((file, index) => (
                                    <Text key={index} style={styles.attachmentItem}>
                                        • {file.filename}
                                    </Text>
                                ))
                            ) : (
                                <Text style={styles.noAttachments}>No unit load files available</Text>
                            )}
                        </View>
                    </View> */}
                </View>
            </View>
        </View>
    );
}; 
